import React, { useState, useRef } from "react";
import { Edit2, User, Check, X, RotateCw, Move, Crop } from "lucide-react";
import CircularButton from "../CircularButton";
import { useTranslation } from "react-i18next";

const EditableUserAvatar = ({
  label,
  image = null,
  onChange,
  size = 120,
  name = "",
}: any) => {
  const [t] = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tempImage, setTempImage]: any = useState(null);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const fileInputRef: any = useRef(null);
  const canvasRef: any = useRef(null);

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader: any = new FileReader();
      reader.onloadend = () => {
        setTempImage(reader?.result);
        setIsEditing(true);
        setRotation(0);
        setPosition({ x: 0, y: 0 });
        setScale(1);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handleMouseDown = (e: any) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: any) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img:any = new Image();
    
    img.onload = () => {
      // Use 2x size for high quality output
      const outputSize = size * 2;
      canvas.width = outputSize;
      canvas.height = outputSize;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Create circular clipping path
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      
      ctx.save();
      
      // Scale transformations by 2x for high-res output
      const scaleFactor = 2;
      
      // Set up transformations
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(scale, scale);
      ctx.translate(position.x * scaleFactor, position.y * scaleFactor);
      
      // Draw image centered
      ctx.drawImage(
        img,
        -img.width / 2,
        -img.height / 2,
        img.width,
        img.height
      );
      
      ctx.restore();
      
      // Convert canvas to Blob
      canvas.toBlob((blob:any) => {
        if (blob && onChange) {
          const file = new File([blob], 'avatar.png', {
            type: 'image/png',
            lastModified: Date.now()
          });
          
          const imageUrl = URL.createObjectURL(blob);
          onChange(imageUrl, file);
        }
        
        setIsEditing(false);
        setTempImage(null);
        setRotation(0);
        setPosition({ x: 0, y: 0 });
        setScale(1);
      }, 'image/png', 0.95);
    };
    
    img.src = tempImage;
  };

  const handleCancel = () => {
    setIsEditing(false);
    setTempImage(null);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    setScale(1);
  };

  const getInitials = () => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const containerStyle: any = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "16px",
    position: "relative",
  };

  const labelStyle: any = {
    marginBottom: "12px",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#666",
    textAlign: "center",
  };

  const avatarContainerStyle: any = {
    position: "relative",
    width: `${size}px`,
    height: `${size}px`,
    cursor: isEditing ? "default" : "pointer",
  };

  const avatarStyle: any = {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    backgroundColor: isHovered ? "#e8e8e8" : "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    transition: "background-color 0.2s ease",
    border: "2px solid #e0e0e0",
  };

  const imageStyle: any = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  };

  const initialsStyle: any = {
    fontSize: `${size / 3}px`,
    fontWeight: 600,
    color: "#666",
    userSelect: "none",
  };

  const overlayStyle: any = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: isHovered ? 1 : 0,
    transition: "opacity 0.2s ease",
  };

  const editButtonStyle: any = {
    position: "absolute",
    bottom: "4px",
    right: "4px",
    padding: "8px",
    backgroundColor: "#f5f5f5",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    transition: "background-color 0.2s ease",
    width: "36px",
    height: "36px",
  };

  const hiddenInputStyle = {
    display: "none",
  };

  const editorModalStyle: any = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "20px",
  };

  const editorContentStyle: any = {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "24px",
    maxWidth: "600px", // Increased from 500px
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
    maxHeight: "90vh", // Add max height
    overflowY: "auto", // Add scroll if needed
  };

  const previewContainerStyle:any = {
    position: 'relative',
    width: `${size * 2.3}px`,
    height: `${size * 2.3}px`,
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: '#f5f5f5',
    border: '2px solid #e0e0e0',
    cursor: 'move'
    // cursor: isDragging ? 'grabbing' : 'grab'
  };

  const previewImageStyle:any = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) rotate(${rotation}deg) scale(${scale})`,
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    transition: isDragging ? 'none' : 'transform 0.2s ease',
    userSelect: 'none',
    pointerEvents: 'none'
  };

  const controlsStyle: any = {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    justifyContent: "center",
  };

  const controlButtonStyle: any = {
    padding: "10px 16px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#f5f5f5",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "0.875rem",
    fontWeight: 500,
    transition: "background-color 0.2s ease",
    color: "#333",
  };

  return (
    <>
      <div style={containerStyle}>
        {label && <div style={labelStyle}>{label}</div>}
        <div
          style={avatarContainerStyle}
          onMouseEnter={() => !isEditing && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => !isEditing && handleEditClick()}
        >
          <div style={avatarStyle}>
            {image ? (
              <img src={image} alt="Avatar" style={imageStyle} />
            ) : name ? (
              <div style={initialsStyle}>{getInitials()}</div>
            ) : (
              <User size={size / 2.5} color="#999" />
            )}
          </div>

          {isHovered && image && !isEditing && (
            <div style={overlayStyle}>
              <Edit2 size={size / 4} color="white" />
            </div>
          )}

          {!isEditing && (
            <button
              style={{
                ...editButtonStyle,
                backgroundColor: isHovered ? "#e8e8e8" : "#f5f5f5",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick();
              }}
            >
              <Edit2 size={16} color="#666" />
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={hiddenInputStyle}
          />
        </div>
      </div>

      {isEditing && (
        <div style={editorModalStyle}>
          <div style={editorContentStyle}>
            <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>
              Edit Photo
            </h3>

            <div
              style={previewContainerStyle}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <img
                src={tempImage}
                alt="Preview"
                style={previewImageStyle}
                draggable={false}
              />
            </div>

            <div style={controlsStyle}>
              <button
                style={controlButtonStyle}
                onClick={handleRotate}
                onMouseEnter={(e: any) =>
                  (e.currentTarget.style.backgroundColor = "#e8e8e8")
                }
                onMouseLeave={(e: any) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
              >
                <RotateCw size={16} />
                Rotate
              </button>

              <button
                style={controlButtonStyle}
                onClick={handleZoomIn}
                onMouseEnter={(e: any) =>
                  (e.currentTarget.style.backgroundColor = "#e8e8e8")
                }
                onMouseLeave={(e: any) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
              >
                <Crop size={16} />
                Zoom In
              </button>

              <button
                style={controlButtonStyle}
                onClick={handleZoomOut}
                onMouseEnter={(e: any) =>
                  (e.currentTarget.style.backgroundColor = "#e8e8e8")
                }
                onMouseLeave={(e: any) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
              >
                <Crop size={16} />
                Zoom Out
              </button>
            </div>

            <div
              style={{
                fontSize: "0.75rem",
                color: "#666",
                textAlign: "center",
              }}
            >
              <Move
                size={14}
                style={{ display: "inline", marginRight: "4px" }}
              />
              Drag to adjust position
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <CircularButton
                  label={t("CANCEL")}
                  manageClick={handleCancel}
              />
              <CircularButton
                  label={t("SAVE")}
                  manageClick={handleSave}
              />
            </div>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </>
  );
};

export default EditableUserAvatar;
